import { AccountProductCategoryId, ProductCategoryId } from "./ProductCategoryId";
import { ProductGroupHierachy } from "./ProductGroupHierachy";
import { AdjustmentProductIds, ProductSizeIdOptions } from "./Seating";
import { UserActionLog } from "./User";

export enum GroupProductsPaymentSplitType {
  ProRata = 1,
  ManualSplit,
}

export interface ProductTagAttributes {
  display: boolean;
  value: string;
  abbreviation: string;
}

export interface ProductTags {
  [tagName: string]: ProductTagAttributes[];
}

export interface ContactDetails {
  firstName?: string;
  lastName?: string;
  position?: string;
  email?: string;
  mobile?: string;
}
export interface CustomerDetails {
  businessName?: string;
  businessStructure?: string;
  businessNumber?: string;
  businessAddress?: string;
  businessCity?: string;
  businessState?: string;
  businessCountry?: string;
  businessPostcode?: string;
  firstName?: string;
  lastName?: string;
  position?: string;
  email?: string;
  mobile?: string;
  enableSecondaryContact?: boolean;
  secondaryContact?: ContactDetails;
  enableBillingContact?: boolean;
  billingContact?: ContactDetails;
  contracts?: any;
  additionalDocuments?: any;
}
export interface TradeReference {
  businessName?: string;
  mobile?: string;
  email?: string;
  addressLine?: string;
  city?: string;
  state?: string;
  referenceCheck?: string;
  tradeReferencesDocuments?: any[];
  enabled?: boolean;
}
export interface Reference {
  tradeReferences?: TradeReference[];
  organisationName?: string;
  referenceCheck?: string;
  addAnotherTradeReference?: boolean;
  creditReferenceDocuments?: any[];
}

export type ImageInfo = {
  ext?: string;
  fileName: string;
  fullPath?: string;
  isChecked?: boolean;
  name?: string;
  sizeFile?: number; // File size in bytes
  sizeThumb?: number; // Thumbnail size in bytes
  thumbUrl?: string; // URL to the thumbnail image
  type?: string; // MIME type of the image
  url: string; // URL to the full image
};
export interface Product {
  _key?: string;
  id: string;
  name: string;
  description: string;
  docketName?: string;
  pluBarcode?: string;
  groupId: string;
  supplier?: string;
  docketSettings?: {
    [restaurantId: string]: {
      [printerId: string]: boolean;
    };
  };
  isComboQuantityIndependentFromOrderItem?: boolean;
  sequence?: number;
  groupProducts?: SizeIdMap<ComboGroup[]>;
  groupProductsPaymentSplitType: GroupProductsPaymentSplitType;
  ingredients: any;
  timeValue?: string;
  image?: ImageInfo;
  overrideGroupDocketSettings?: {
    [restaurantId: string]: boolean;
  };
  orderingRules?: {
    preOrder: boolean;
    preOrderDays: number;
    completionTime: number;
    showSingleSizeInOrdering?: boolean;
    showSingleSizeInDocket?: boolean;
  };
  enableOrderingAppProductFiltering: boolean;
  butlerService: {
    subcategoryId: string;
  };
  translateName: {
    [language: string]: any;
  };
  translateDescription: {
    [language: string]: any;
  };
  salesTax: "Included" | "Exempt";
  sizes: SizeIdMap<boolean>;
  restaurants: {
    [restaurantId: string]: ProductInfo;
  };
  fixedPriceMenuTypes: {
    adult: "adult" | "child";
  };
  fixedMenuPriceSplit?: {
    restaurants: {
      [id: string]: { food: number; beverage: number };
    };
  };
  displayOnMenuOrderingApp?: boolean;
  tags?: ProductTags;
  lastUpdated: number;
  // Product enhancements
  preparations?: ProductPreparation[];
  additions: ProductAddition[];
  modifiers: ProductModifier[];
  upsells: ProductUpsell[];
  upgrades?: ProductUpgrade[];
  typeId: ProductTypeId;
  productCategoryId: ProductCategoryId;
  enabled?: boolean;
  /** Account products only (see `AccountProduct`) — restaurants the product is switched on for. Sale products use `restaurants[id].enabled`. */
  enabledRestaurants?: EnabledRestaurants;
  recipeDescription?: string;
  recipeIngredients?: string;
  recipeMethod?: string;
  additionalInformation?: string;
  productGroupHierachy: ProductGroupHierachy[];
  flags?: ProductFlags;
  customer?: CustomerDetails;
  references?: Reference;
  useComboChildItemsAsSeparateForDocket?: boolean;
  restaurantRecipeIngredients?: string;
  restaurantRecipeMethod?: string;
  isOpenPriceProduct?: boolean; // denotes this product is exclusively used for the purpose of Open price item
  isStandAloneOpenPriceProduct?: boolean; // denotes this product is exclusively used as fallback Open price item
}

export interface Exclusions {
  [productId: string]: {
    [productSizeId: string]: boolean;
  };
}

export interface SpecialProduct {
  productId: string;
  sizeId: string;
  menuId: string;
  headingId: string;
  price: number;
  badgeText: string;
  menuHeadingId: string;
}
export interface GroupHeadingHierachy {
  name: string;
  groupHeadingId: string;
}

export interface ProductSearchResult extends Partial<Product> {
  productId: string;
  objectID?: string; // this holds same value as productId
  uneditableSizes?: { [sizeId: string]: boolean };
}

export interface PriceOptionProducts {
  [productId: string]: PriceOptionProduct;
}
export interface PriceOptionProduct extends ProductSearchResult {
  sizeIdOptions: ProductSizeIdOptions;
}
export enum ProductTypeId {
  food = 1,
  fixedPriceMenu = 2,
  /** @deprecated */
  fixedPrice = 3, //Is this used anymore?
  financial = 4, //Fees & Charges, No Show Fee, CanX Fee, Sunday Surcharge etc
  // credit = 5,
  // pettyCash = 6,
  combinationFoodAndBeverage = 8,
  beverage = 16,
  // noShow = 17,
  // cancellationFee = 18,
  // prepaid = 32,
  extendDuration = 128,
  tableClass = 256,
  // ingredient = 1024,
  depositMinSpend = 2048, // don't show - check for sunil maybe put this against fees/charges min spend adjustment
  marketingPromotional = 4096, //don't show
  customerServiceExpense = 4097, //don't show
  operationalExpense = 4098, //don't show
  pettyCashExpense = 4099, //don't show
  contingentLiability = 4100, //don't show
  deposits = 4101, //don't show
  giftCertificates = 4102, //don't show
  creditAccounts = 4103, //don't show
  cashout = 4104, //don't show
  inventory = 4105, //don't show
}

export type AllowedEnhancementProductTypes = "Beverage" | "Food";

export type SizeIdMap<T> = { [sizeId: string]: T };

// Canonical block of product + its selected sizes
export interface ProductSizeRef {
  productId: string;
  sizes: SizeIdMap<boolean>;
}

export type ComboGroupProduct = {
  sizes: SizeIdMap<boolean>;
  sortSequence?: number;
  productId?: string;
  /** @deprecated use prductId when possible */
  _productKey?: string;
  hidden?: boolean;
};
export type CombinationMap = {
  [productId: string]: ComboGroupProduct;
};
export interface ComboGroup {
  restaurants: {
    [restaurantId: string]: {
      price: number;
    };
  };
  combinations: CombinationMap;
  name?: string;
  quantity: number;
  includeAllCombinations?: boolean;
}

export interface ModifierGroupProduct extends ProductSizeRef {
  order?: number;
}
export interface Modifier {
  id?: string;
  enabled: boolean;
  mandatory?: boolean;
  menuDisplayName?: string;
  // modifierGroupId: string;
  modifierType?: "Beverage" | "Food";
  name: string;
  products?: ModifierGroupProduct[];
  restaurants?: {
    [restaurantId: string]: boolean;
  };
}

export interface ModifierGroups {
  [modifierGroupId: string]: Modifier;
}

export interface AdditionGroupProduct extends ProductSizeRef {
  order: number;
  allowMultipleQuantities: boolean;
}

export interface ProductSize {
  productContainerId?: string;
  value?: number;
  units?: string;
  enabled: boolean;
  name?: string;
  abbreviation?: string;
  id?: string;
  byUnits?: string;
  order: number;
  useContainerAsName?: boolean;
}

export interface ProductSizes {
  [productSizeId: string]: ProductSize;
}
export interface ProductSizeContainer {
  id?: string;
  name: string;
  enabled: boolean;
}

export interface ProductSizeContainers {
  [productSizeContainerId: string]: ProductSizeContainer;
}

export interface PreparationGroup {
  id?: string;
  enabled: boolean;
  name: string;
  menuDisplayName?: string;
  // preparationGroupId: string;
  mandatory?: boolean;
  preparationRequirements?: PreparationGroupProduct[];
  preparationType?: "Beverage" | "Food";
  restaurants?: {
    [restaurantId: string]: boolean;
  };
}

export interface PreparationGroupProduct {
  id: string;
  name: string;
  order: number;
}

export interface PreparationGroups {
  [preparationGroupId: string]: PreparationGroup;
}

export interface UpsellGroupProduct extends ProductSizeRef {
  order: number;
}

export interface UpsellGroup {
  id?: string;
  enabled: boolean;
  name: string;
  products?: UpsellGroupProduct[];
  upsellType?: "Beverage" | "Food";
  restaurants?: {
    [restaurantId: string]: boolean;
  };
}

export interface UpsellGroups {
  [upsellGroupId: string]: UpsellGroup;
}

/** Add Upgrade group types */
export interface UpgradeGroupProduct extends ProductSizeRef {
  order: number;
}

export interface UpgradeGroup {
  id?: string;
  enabled: boolean;
  name: string;
  products?: UpgradeGroupProduct[];
  upgradeType?: AllowedEnhancementProductTypes;
  restaurants?: {
    [restaurantId: string]: boolean;
  };
}

export interface UpgradeGroups {
  [upgradeGroupId: string]: UpgradeGroup;
}

export interface PromotionGroupProduct extends ProductSizeRef {
  order: number;
}

export interface PromotionGroup {
  id?: string;
  enabled: boolean;
  name: string;
  products?: PromotionGroupProduct[];
  promotionType?: AllowedEnhancementProductTypes;
}
export interface PromotionGroups {
  [promotionGroupId: string]: PromotionGroup;
}

export interface Products {
  [productId: string]: Product;
}

export interface PriceOption {
  _key?: string;
  id?: string;
  adjustmentProductIds?: AdjustmentProductIds;
  applyToAllProducts?: boolean;
  enabled: boolean;
  enabledRestaurantIds: string[];
  name: string;
  nearest: number;
  priceAdjustment: number;
  rounding: "roundUp" | "";
  showWithStandardPrice?: boolean;
  updateLog: UserActionLog<PriceOption>[];
  lastUpdated: number;
  accountProductId?: string;
}

export interface PriceOptions {
  [priceOptionId: string]: PriceOption;
}

export interface ProductTag {
  id: string;
  name: string;
  /** @deprecated use ordering and booking instead */
  values?: {
    [tag: string]: boolean;
  };
  restaurantId: string;
  ordering: NameMap<boolean>;
  booking: NameMap<boolean>;
}

export interface AdditionGroup {
  id?: string;
  additionType?: "Beverage" | "Food";
  enabled?: boolean;
  name: string;
  products?: AdditionGroupProduct[];
  restaurants?: {
    [restaurantId: string]: boolean;
  };
}

export interface AdditionGroups {
  [additionGroupId: string]: AdditionGroup;
}

export interface AccountMappingItem {
  productId: string;
  groupId: string;
}

export interface AccountMappings {
  // Acounts
  cashoutProductId: string;
  deferredVoucherProductId: string;
  functionInvoiceProductId: string;
  functionOutstandingInvoiceProductId: string;
  noItemProductId?: string;
  giftCertificatePromotionProductId: string;
  refundProductId: string;
  tabProductId: string;
  wastageProductId: string;
  accounts: string[];

  //Payment Price Adjustments
  paymentPriceDecreaseAdjustments: string[];
  paymentPriceIncreaseAdjustments: string[];
  paymentPriceSurchagesAdjustments: string[];

  //Credit
  creditAccountGroupId: string;
  contraGroupId: string;

  // Sale
  giftCertificateProductId: string;
  cancellationFeeProductId: string;
  noShowFeeProductId?: string;
  expiredGiftCertificateProductId: string;
  expiredDeferredBookingCertificateProductId: string;

  // Membership
  membershipCashbackProductId?: string;
  membershipStampCardProductId?: string;
  membershipVoucherProductId?: string;
  membershipGiftCertificateId?: string;

  // Open Products
  openProductFoodId?: string;
  openProductBeverageId?: string;
  openProductFoodAndBeverageId?: string;
  openProductRetailId?: string;
}

export interface PackageOverridePricesProduct {
  [productId: string]: SizeIdMap<number>;
}

export enum ProductGroupPriceAdjustmentType {
  Increase = "increase",
  Decrease = "decrease",
}
export interface ProductGroup {
  id?: string;
  _key?: string;
  calculationTypeId: any;
  categoryId: string;
  description: string;
  enableButlerService?: boolean;
  enabled?: boolean;
  name: string;
  sequence?: number;
  parentGroup: string;
  docketSettings?: {
    [restaurantId: string]: {
      [printerId: string]: boolean;
    };
  };
  overrideGroupDocketSettings?: {
    [restaurantId: string]: boolean;
  };
  requiredPrepayment?: boolean;
  requiresTextInput?: boolean;
  textInput: any;
  productCategoryId: ProductCategoryId;
  systemGroup?: boolean;
  lastUpdated: number;
  productTypeId?: ProductTypeId;
}

export interface ProductGroups {
  [productGroupId: string]: ProductGroup;
}

export interface Pricing {
  [productId: string]: SizeIdMap<number>;
}

export enum StockStatus {
  SoldOut = "Sold Out",
  Hide = "Hide",
}

/**
 * - All: Applies to all menus in the restaurant.
 * - Menus: Applies to specific menus only.
 */
export enum StockScope {
  All = "all",
  Menus = "menus",
}

export interface StockByType {
  products: StockByProductId;
  specials: StockByProductId;
}

export interface StockByProductId {
  [id: string]: ProductStock;
}

export interface ProductStock {
  productId: string;
  createdAt: number;
  updatedAt: number;
  date: number;
  restaurantId: string;
  stockScope: StockScope;
  enabledMenus?: Record<
    string,
    {
      enabled: boolean; //
      groupHeadingId?: string /** Only used /relevant if its under /special product */;
    }
  >;
  sizes: Record<string, SizeStock>;
  removed?: boolean; //only used in frontend for special products to determine whether this was removed or not.
}

export interface SizeStock {
  enabled: boolean;
  /**
   * - true: Status (Sold/Hide) applies only after limit is reached
   * - false: Status applies directly regardless of stock
   */
  isStockLimitEnabled: boolean;
  /**
   * Behavior depends on `isStockLimitEnabled` and `limit` or `menuLimit`.
   */
  status: StockStatus;
  limit?: number /* Used when `isStockLimitEnabled` is true */;
  sold?: number /* Used when `isStockLimitEnabled` is true */;
  price?: number /** Only used/relevant if its under /special product */;
  badgeText?: string /** Only used/relevant if its under /special product */;
}

export type ProductFlags = {
  useAsAddition?: boolean;
  useAsModifier?: boolean;
};

export type ProductPreparation = {
  mandatory?: boolean;
  menuDisplayName?: string;
  preparationGroupId: string;
  totalselections?: number;
  limitedSelection?: boolean;
  exclusions?: {
    [preparationRequirementId: string]: boolean;
  };
  sizes?: SizeIdMap<boolean>;
};

export interface ProductModifier {
  mandatory?: boolean;
  menuDisplayName?: string;
  modifierGroupId: string;
  exclusions: Exclusions;
}

export interface ProductAddition {
  mandatory?: boolean;
  menuDisplayName?: string;
  additionGroupId: string;
  totalselections?: number;
  limitedSelection?: boolean;
  exclusions: Exclusions;
}

export interface ProductUpsell {
  menuDisplayName?: string;
  upsellGroupId: string;
  exclusions: Exclusions;
}

export interface ProductUpgrade {
  menuDisplayName: string;
  upgradeGroupId: string;
  exclusions: Exclusions;
}

export interface ProductInfo {
  enabled: boolean;
  description?: string;
  image?: ImageInfo;
  price: SizeIdMap<number> | number; // some product's don't have sizes
  sizes?: SizeIdMap<number>;
  noSaleItem?: SizeIdMap<boolean>;
}

export type NameMap<T> = { [name: string]: T };

// type for src/resbutler-utils/files/foodIntolerances.json
export interface FoodIntolerance {
  name: string;
  abbreviation?: string;
}

interface DietaryRequirement extends FoodIntolerance {
  abbreviation?: string;
}

interface FishAndSeafood {
  "Fish & Seafood": FoodIntolerance[];
}

interface MeatAndPoultry {
  "Meat & Poultry": FoodIntolerance[];
}

interface AdditiveCategory {
  Additives: FoodIntolerance[];
  Oils: FoodIntolerance[];
}

interface SavoryFood {
  Savoury: FoodIntolerance[];
}

interface SweetsAndSnacks {
  "Sweets & Snacks": FoodIntolerance[];
}

interface Drinks {
  "Non-Alcoholic": FoodIntolerance[];
  Alcohol: FoodIntolerance[];
}

export interface FoodIntolerances {
  Allergies: FoodIntolerance[];
  "Dietary Requirements": DietaryRequirement[];
  "Religious Preparation": FoodIntolerance[];
  Sourcing: FoodIntolerance[];
  "Primary Ingredients": (FoodIntolerance | FishAndSeafood | MeatAndPoultry)[];
  "Ingredients of Concern": AdditiveCategory[];
  "Food Types": (SavoryFood | SweetsAndSnacks)[];
  "Drink Types": Drinks[];
  "Cooking Preparations": FoodIntolerance[];
}

/** Define Unit Price Information of Product  */
export interface ProductPriceInfo {
  standardPrice: number; // product price as setup in office without any adjustments
  premiumStandardPrice: number; // standardPrice + all premium price options (increase) but without discounts (decrease options like happy hour)
  finalPrice: number; // final payable price
  appliedPriceOptions?: PriceOption[];
}

export interface OpenPriceSetups {
  [id: string]: OpenPriceSetup;
}

export interface OpenPriceSetup {
  id: string;
  // productId: string;
  // productSizeId: string;
  restaurantId: string;
  rootProductGroupIds: string[];
  additionalProductGroupIds: string[];
  _key?: string; // clone of id above
}

export type ProductEnhancementType = "additions" | "modifiers" | "upsells" | "preparations";
// Generic type that extends T and adds a metaData property of type G
type ExtendedEnhancementGroup<T> = T & {
  productsMetaData: Product[];
};

type ExtendedProductEnhancement<T, G> = T & {
  metaData: G;
};

type ExtendedProductPreparation = ExtendedProductEnhancement<ProductPreparation, ExtendedEnhancementGroup<PreparationGroup>>;
type ExtendedProductAddition = ExtendedProductEnhancement<ProductAddition, ExtendedEnhancementGroup<AdditionGroup>>;
type ExtendedProductModifier = ExtendedProductEnhancement<ProductModifier, ExtendedEnhancementGroup<Modifier>>;
type ExtendedProductUpsell = ExtendedProductEnhancement<ProductUpsell, ExtendedEnhancementGroup<UpsellGroup>>;
type ExtendedProductUpgrade = ExtendedProductEnhancement<ProductUpgrade, ExtendedEnhancementGroup<UpgradeGroup>>;

export interface EnrichedProductMetaData {
  preparations?: ExtendedProductPreparation[];
  additions?: ExtendedProductAddition[];
  modifiers?: ExtendedProductModifier[];
  upsells?: ExtendedProductUpsell[];
  upgrades?: ExtendedProductUpgrade[];
  groupProducts?: SizeIdMap<ComboGroup[]>;
  groupId: string;
  typeId: string;
  sizes: SizeIdMap<boolean>;
  productCategoryId: string;
  restaurants: {
    [restaurantId: string]: ProductInfo;
  };
}
export interface EnrichedProduct extends Product {
  metaData: EnrichedProductMetaData;
}

// { [restaurantId]: true } — only enabled restaurants are stored; disabling deletes the key
export interface EnabledRestaurants {
  [restaurantId: string]: true;
}

// account (non-sale) product — the only kind that carries `enabledRestaurants`
export type AccountProduct = Product & { productCategoryId: AccountProductCategoryId; enabledRestaurants?: EnabledRestaurants };

// every other product category — `enabledRestaurants` must never be set
export type NonAccountProduct = Omit<Product, "enabledRestaurants"> & { productCategoryId: Exclude<ProductCategoryId, AccountProductCategoryId>; enabledRestaurants?: never };
